import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { 
  FaUsers, FaEye, FaCheck, FaTimes, FaBan,
  FaSearch, FaFilter, FaPhoneAlt, FaEnvelope,
  FaBuilding, FaMapMarkerAlt, FaClock, FaInfoCircle,
  FaUserTie, FaBriefcase, FaIdCard, FaToggleOn, FaToggleOff,
  FaChevronLeft, FaChevronRight, FaBook, FaDownload
} from 'react-icons/fa';

const AppTilesDealerManagement = () => {
  const { user } = useContext(AuthContext);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState({ all: 0, pending: 0, approved: 0, suspended: 0 });
  const [exportLoading, setExportLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Dark mode - syncs with DashboardLayout via localStorage
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'darkMode') setDarkMode(e.newValue === 'true');
    };
    window.addEventListener('storage', handleStorage);
    // Polling fallback for same-tab changes
    const interval = setInterval(() => {
      const saved = localStorage.getItem('darkMode');
      setDarkMode(saved === 'true');
    }, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  const fetchDealers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        page: currentPage,
        limit: 15,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      };

      const response = await axios.get(`${API_BASE}/app-dealer-management`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setDealers(response.data.data.dealers || []);
        setTotalPages(response.data.data.totalPages || response.data.data.pagination?.totalPages || 1);
        setTotalItems(response.data.data.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.error('Error fetching dealers:', error);
      Swal.fire('Error', 'Failed to fetch dealers', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchTerm, API_BASE]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const statuses = ['all', 'pending', 'approved', 'suspended'];
      const results = await Promise.all(
        statuses.map(s =>
          axios.get(`${API_BASE}/app-dealer-management`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: 1, limit: 1, ...(s !== 'all' && { status: s }) }
          }).then(r => ({ status: s, count: r.data.data?.pagination?.totalItems || 0 }))
            .catch(() => ({ status: s, count: 0 }))
        )
      );
      const newStats = {};
      results.forEach(r => { newStats[r.status] = r.count; });
      setStats(newStats);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  }, [API_BASE]);

  useEffect(() => { fetchDealers(); }, [fetchDealers]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDealers();
  };

  const handleApprove = async (dealerId, dealerName) => {
    const result = await Swal.fire({
      title: 'Approve Dealer?',
      html: `Approve <strong>${dealerName || 'this dealer'}</strong> for your company?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Approve',
      background: darkMode ? '#1f2937' : '#fff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
    });
    if (!result.isConfirmed) return;
    setActionLoading(dealerId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE}/app-dealer-management/${dealerId}/approval`,
        { action: 'approve' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        Swal.fire({ title: 'Approved!', text: 'Dealer has been approved successfully.', icon: 'success', background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#f3f4f6' : '#1f2937' });
        fetchDealers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error approving dealer:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to approve dealer', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (dealerId, dealerName) => {
    const result = await Swal.fire({
      title: 'Reject Dealer?',
      html: `Reject <strong>${dealerName || 'this dealer'}</strong>? This will remove their registration.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Reject',
      background: darkMode ? '#1f2937' : '#fff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
    });
    if (!result.isConfirmed) return;
    setActionLoading(dealerId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE}/app-dealer-management/${dealerId}/approval`,
        { action: 'reject' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        Swal.fire({ title: 'Rejected', text: 'Dealer registration has been rejected.', icon: 'success', background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#f3f4f6' : '#1f2937' });
        fetchDealers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error rejecting dealer:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to reject dealer', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (dealerId, dealerName) => {
    const result = await Swal.fire({
      title: 'Suspend Dealer?',
      html: `Suspend <strong>${dealerName || 'this dealer'}</strong>? They will lose app access.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Suspend',
      background: darkMode ? '#1f2937' : '#fff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
    });
    if (!result.isConfirmed) return;
    setActionLoading(dealerId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE}/app-dealer-management/${dealerId}/status`,
        { isActive: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        Swal.fire({ title: 'Suspended', text: 'Dealer has been suspended.', icon: 'success', background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#f3f4f6' : '#1f2937' });
        fetchDealers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error suspending dealer:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to suspend dealer', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = async (dealerId, dealerName) => {
    const result = await Swal.fire({
      title: 'Activate Dealer?',
      html: `Re-activate <strong>${dealerName || 'this dealer'}</strong>? They will regain app access.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Activate',
      background: darkMode ? '#1f2937' : '#fff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
    });
    if (!result.isConfirmed) return;
    setActionLoading(dealerId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE}/app-dealer-management/${dealerId}/status`,
        { isActive: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        Swal.fire({ title: 'Activated!', text: 'Dealer has been re-activated.', icon: 'success', background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#f3f4f6' : '#1f2937' });
        fetchDealers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error activating dealer:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to activate dealer', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const viewDealerDetails = (dealer) => {
    setSelectedDealer(dealer);
    setShowDetails(true);
  };

  const handleExportPending = async () => {
    const confirm = await Swal.fire({
      title: 'Export Pending Dealers',
      html: `<p>This will export all <strong>${stats.pending || 0}</strong> pending dealer registrations to a CSV file.</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Export CSV',
      background: darkMode ? '#1f2937' : '#fff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
    });
    if (!confirm.isConfirmed) return;

    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/app-dealer-management`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 10000, status: 'pending' }
      });

      const pendingDealers = response.data?.data?.dealers || [];
      if (pendingDealers.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There are no pending dealers to export.', icon: 'info', background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#f3f4f6' : '#1f2937' });
        return;
      }

      // Build CSV
      const headers = [
        'Dealer ID', 'Full Name', 'Phone Number', 'Email',
        'Address', 'Station', 'Gender', 'Aadhaar', 'PAN',
        'Business Legal Name', 'Business Trade Name', 'GSTIN', 'Business PAN',
        'Business Phone', 'Business Email', 'Registered Office Address',
        'Has Manager', 'Manager Name', 'Manager Phone', 'Manager Email',
        'Company', 'Registered At'
      ];

      const escapeCSV = (val) => {
        if (val == null) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const rows = pendingDealers.map(d => {
        const dd = d.dealerDetails || {};
        const bd = d.businessDetails || {};
        const md = d.managerDetails || {};
        return [
          d.id,
          dd.fullName || d.dealerName,
          dd.mobileNumber || d.phoneNumber || d.primaryPhoneNumber,
          dd.emailAddress || d.email,
          dd.address,
          dd.station?.name,
          dd.gender,
          dd.aadhaar,
          dd.pan,
          bd.legalName,
          bd.tradeName,
          bd.gstin,
          bd.pan,
          bd.businessPhoneNumber,
          bd.businessEmailId,
          bd.registeredOfficeAddress,
          md.hasManager ? 'Yes' : 'No',
          md.fullName,
          md.mobileNumber,
          md.emailAddress,
          d.companyName,
          d.registeredAt ? new Date(d.registeredAt).toLocaleDateString('en-IN') : (d.createdAt ? new Date(d.createdAt).toLocaleDateString('en-IN') : '')
        ].map(escapeCSV).join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `pending-dealers-${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({ title: 'Exported!', text: `Successfully exported ${pendingDealers.length} pending dealer(s).`, icon: 'success', background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#f3f4f6' : '#1f2937' });
    } catch (error) {
      console.error('Error exporting pending dealers:', error);
      Swal.fire('Error', 'Failed to export pending dealers', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        bg: darkMode ? 'bg-yellow-900/40' : 'bg-yellow-100', 
        text: darkMode ? 'text-yellow-300' : 'text-yellow-800', 
        dot: darkMode ? 'bg-yellow-400' : 'bg-yellow-400', 
        label: 'Pending' 
      },
      approved: { 
        bg: darkMode ? 'bg-green-900/40' : 'bg-green-100', 
        text: darkMode ? 'text-green-300' : 'text-green-800', 
        dot: darkMode ? 'bg-green-400' : 'bg-green-400', 
        label: 'Approved' 
      },
      suspended: { 
        bg: darkMode ? 'bg-red-900/40' : 'bg-red-100', 
        text: darkMode ? 'text-red-300' : 'text-red-800', 
        dot: darkMode ? 'bg-red-400' : 'bg-red-400', 
        label: 'Suspended' 
      },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${config.dot}`}></span>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statCards = [
    { key: 'all', label: 'Total Dealers', color: 'from-blue-500 to-blue-600', icon: <FaUsers /> },
    { key: 'pending', label: 'Pending', color: 'from-yellow-500 to-amber-600', icon: <FaClock /> },
    { key: 'approved', label: 'Approved', color: 'from-green-500 to-emerald-600', icon: <FaCheck /> },
    { key: 'suspended', label: 'Suspended', color: 'from-red-500 to-rose-600', icon: <FaBan /> },
  ];

  // Reusable class helpers
  const cardBg = darkMode 
    ? 'bg-gray-800/80 backdrop-blur-sm border-gray-700/50' 
    : 'bg-white border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textMuted = darkMode ? 'text-gray-500' : 'text-gray-400';

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto transition-colors duration-300">
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-2xl md:text-3xl font-bold flex items-center ${textPrimary}`}>
          <FaUsers className="mr-3 text-blue-500" />
          App Dealer Management
        </h1>
        <p className={`mt-1 ${textSecondary}`}>Manage app dealer registrations, approvals, and access</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        {statCards.map(card => (
          <button
            key={card.key}
            onClick={() => { setStatusFilter(card.key); setCurrentPage(1); }}
            className={`rounded-xl border p-4 transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${cardBg} ${
              statusFilter === card.key 
                ? (darkMode ? 'ring-2 ring-blue-400 border-blue-400/50' : 'ring-2 ring-blue-500 border-blue-500') 
                : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className={`text-xs font-medium uppercase tracking-wide ${textSecondary}`}>{card.label}</p>
                <p className={`text-2xl font-bold mt-1 ${textPrimary}`}>{stats[card.key] || 0}</p>
              </div>
              <div className={`bg-gradient-to-br ${card.color} text-white p-3 rounded-lg text-lg shadow-lg`}>
                {card.icon}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Search and Filter Bar */}
      <div className={`p-4 rounded-xl border shadow-sm mb-6 ${cardBg}`}>
        <div className="flex flex-col md:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${textMuted}`} />
              <input
                type="text"
                placeholder="Search by dealer name, phone, or business name..."
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors ${
                  darkMode 
                    ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </form>
          
          <div className="flex gap-2">
            <select
              className={`px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors ${
                darkMode 
                  ? 'bg-gray-700/50 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="suspended">Suspended</option>
            </select>
            
            <button
              type="submit"
              onClick={handleSearch}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center text-sm font-medium transition-colors"
            >
              <FaFilter className="mr-2" />
              Search
            </button>
            
            <button
              onClick={handleExportPending}
              disabled={exportLoading || !stats.pending}
              className={`px-4 py-2.5 rounded-lg flex items-center text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                darkMode
                  ? 'bg-yellow-600 text-white hover:bg-yellow-500'
                  : 'bg-yellow-500 text-white hover:bg-yellow-600'
              }`}
              title={stats.pending ? `Export ${stats.pending} pending dealer(s) to CSV` : 'No pending dealers to export'}
            >
              {exportLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <FaDownload className="mr-2" />
              )}
              Export Pending
            </button>
          </div>
        </div>
      </div>

      {/* Dealers Table */}
      <div className={`rounded-xl border shadow-sm overflow-hidden ${cardBg}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={darkMode ? 'bg-gray-900/50 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200'}>
                {['Dealer Info', 'Contact', 'Business', 'Status', 'Registered'].map(h => (
                  <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                ))}
                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody className={darkMode ? 'divide-y divide-gray-700/50' : 'divide-y divide-gray-100'}>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
                      <span className={textSecondary}>Loading dealers...</span>
                    </div>
                  </td>
                </tr>
              ) : dealers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FaUsers className={`text-4xl mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`font-medium ${textSecondary}`}>No dealers found</p>
                      <p className={`text-sm mt-1 ${textMuted}`}>
                        {statusFilter !== 'all' 
                          ? `No ${statusFilter} dealers. Try changing the filter.`
                          : 'No dealers have registered yet.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                dealers.map((dealer) => (
                  <tr key={dealer._id} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`}>
                    {/* Dealer Info */}
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                          <span className={`font-bold text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                            {(dealer.dealerName || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className={`text-sm font-semibold truncate ${textPrimary}`} title={dealer.dealerName}>
                            {dealer.dealerName || '—'}
                          </div>
                          <div className={`text-xs font-mono ${textMuted}`}>{dealer.id}</div>
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="px-4 py-3">
                      <div className={`text-sm flex items-center ${textPrimary}`}>
                        <FaPhoneAlt className={`mr-1.5 text-xs flex-shrink-0 ${textMuted}`} />
                        <span className="truncate">{dealer.phoneNumber || dealer.primaryPhoneNumber || '—'}</span>
                      </div>
                      {dealer.email && (
                        <div className={`text-xs flex items-center mt-1 ${textSecondary}`}>
                          <FaEnvelope className={`mr-1.5 text-xs flex-shrink-0 ${textMuted}`} />
                          <span className="truncate">{dealer.email}</span>
                        </div>
                      )}
                    </td>

                    {/* Business */}
                    <td className="px-4 py-3">
                      <div className={`text-sm truncate max-w-[180px] ${textPrimary}`} title={dealer.businessDetails?.legalName || dealer.businessDetails?.tradeName}>
                        {dealer.businessDetails?.legalName || dealer.businessDetails?.tradeName || '—'}
                      </div>
                      {dealer.businessDetails?.gstin && (
                        <div className={`text-xs mt-0.5 ${textSecondary}`}>
                          GSTIN: {dealer.businessDetails.gstin}
                        </div>
                      )}
                      {dealer.companyName && (
                        <span className={`inline-flex items-center mt-1 px-1.5 py-0.5 text-xs rounded ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                          {dealer.companyName}
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      {getStatusBadge(dealer.status)}
                      {dealer.approvedAt && (
                        <div className={`text-xs mt-1 ${textMuted}`}>
                          {formatDate(dealer.approvedAt)}
                        </div>
                      )}
                    </td>

                    {/* Registered */}
                    <td className="px-4 py-3">
                      <div className={`text-sm flex items-center ${textSecondary}`}>
                        <FaClock className={`mr-1.5 text-xs ${textMuted}`} />
                        {formatDate(dealer.registeredAt || dealer.createdAt)}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onClick={() => viewDealerDetails(dealer)}
                          className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-50'}`}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        
                        {dealer.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(dealer._id, dealer.dealerName)}
                              disabled={actionLoading === dealer._id}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${darkMode ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-50'}`}
                              title="Approve"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={() => handleReject(dealer._id, dealer.dealerName)}
                              disabled={actionLoading === dealer._id}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'}`}
                              title="Reject"
                            >
                              <FaTimes />
                            </button>
                          </>
                        )}
                        
                        {dealer.status === 'approved' && (
                          <button
                            onClick={() => handleSuspend(dealer._id, dealer.dealerName)}
                            disabled={actionLoading === dealer._id}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${darkMode ? 'text-yellow-400 hover:bg-yellow-900/30' : 'text-yellow-600 hover:bg-yellow-50'}`}
                            title="Suspend"
                          >
                            <FaToggleOff />
                          </button>
                        )}
                        
                        {dealer.status === 'suspended' && (
                          <button
                            onClick={() => handleActivate(dealer._id, dealer.dealerName)}
                            disabled={actionLoading === dealer._id}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${darkMode ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-50'}`}
                            title="Activate"
                          >
                            <FaToggleOn />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`px-4 py-3 flex items-center justify-between border-t ${darkMode ? 'bg-gray-900/30 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <div className={`text-sm ${textSecondary}`}>
              Page {currentPage} of {totalPages} ({totalItems} total)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 border text-sm rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  darkMode 
                    ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                <FaChevronLeft className="mr-1 text-xs" /> Prev
              </button>
              
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 2);
                let end = Math.min(totalPages, currentPage + 2);
                if (start > 1) pages.push(<span key="s" className={`px-1 ${textMuted}`}>...</span>);
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        i === currentPage 
                          ? 'bg-blue-600 text-white' 
                          : darkMode 
                            ? 'border border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700' 
                            : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
                if (end < totalPages) pages.push(<span key="e" className={`px-1 ${textMuted}`}>...</span>);
                return pages;
              })()}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 border text-sm rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  darkMode 
                    ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Next <FaChevronRight className="ml-1 text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dealer Details Modal */}
      {showDetails && selectedDealer && (
        <DealerDetailsModal
          dealer={selectedDealer}
          darkMode={darkMode}
          onClose={() => setShowDetails(false)}
          onApprove={handleApprove}
          onReject={handleReject}
          onSuspend={handleSuspend}
          onActivate={handleActivate}
          actionLoading={actionLoading}
          getStatusBadge={getStatusBadge}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

// ─── Dealer Details Modal ──────────────────────────────────────
const DealerDetailsModal = ({ dealer, darkMode, onClose, onApprove, onReject, onSuspend, onActivate, actionLoading, getStatusBadge, formatDate }) => {
  const d = dealer.dealerDetails || {};
  const m = dealer.managerDetails || {};
  const b = dealer.businessDetails || {};

  const modalBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const sectionBg = darkMode ? 'bg-gray-900/50' : 'bg-gray-50';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  const InfoRow = ({ label, value, icon }) => {
    if (!value) return null;
    return (
      <div className={`flex items-start py-2 border-b last:border-b-0 ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
        {icon && <span className={`mr-2 mt-0.5 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{icon}</span>}
        <div className="min-w-0">
          <span className={`text-xs uppercase tracking-wide ${textSecondary}`}>{label}</span>
          <p className={`text-sm font-medium break-words ${textPrimary}`}>{value}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 pt-10">
      <div className={`${modalBg} rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border ${borderColor}`}>
        {/* Modal Header */}
        <div className={`sticky top-0 ${modalBg} border-b ${borderColor} px-6 py-4 flex justify-between items-center z-10 rounded-t-2xl`}>
          <div className="flex items-center">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center mr-4 ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
              <span className={`font-bold text-lg ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                {(dealer.dealerName || '?').charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className={`text-xl font-bold ${textPrimary}`}>{dealer.dealerName || 'Dealer Details'}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-sm font-mono ${textSecondary}`}>{dealer.id}</span>
                {getStatusBadge(dealer.status)}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
          >
            <FaTimes className="text-lg" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Dealer Personal Details */}
            <div className={`${sectionBg} p-5 rounded-xl`}>
              <h4 className={`font-semibold mb-4 flex items-center text-sm uppercase tracking-wide ${textPrimary}`}>
                <FaUserTie className="mr-2 text-blue-500" />
                Dealer Details
              </h4>
              <div className="space-y-1">
                <InfoRow label="Full Name" value={d.fullName} />
                <InfoRow label="Mobile Number" value={d.mobileNumber} icon={<FaPhoneAlt />} />
                <InfoRow label="Alternate Phone" value={d.alternatePhoneNumber} icon={<FaPhoneAlt />} />
                <InfoRow label="Email" value={d.emailAddress} icon={<FaEnvelope />} />
                <InfoRow label="Gender" value={d.gender} />
                <InfoRow label="Date of Birth" value={d.dateOfBirth ? new Date(d.dateOfBirth).toLocaleDateString('en-IN') : null} />
                <InfoRow label="Address" value={d.address} icon={<FaMapMarkerAlt />} />
                <InfoRow label="Station" value={d.station?.name} icon={<FaMapMarkerAlt />} />
                <InfoRow label="Located in Main Station" value={d.isLocatedInMainStation === true ? 'Yes' : d.isLocatedInMainStation === false ? 'No' : null} />
                <InfoRow label="Aadhaar" value={d.aadhaar} icon={<FaIdCard />} />
                <InfoRow label="PAN" value={d.pan} icon={<FaIdCard />} />
              </div>
            </div>

            {/* Registration Info */}
            <div className="space-y-6">
              <div className={`${sectionBg} p-5 rounded-xl`}>
                <h4 className={`font-semibold mb-4 flex items-center text-sm uppercase tracking-wide ${textPrimary}`}>
                  <FaInfoCircle className="mr-2 text-blue-500" />
                  Registration Info
                </h4>
                <div className="space-y-1">
                  <InfoRow label="Primary Phone" value={dealer.primaryPhoneNumber} icon={<FaPhoneAlt />} />
                  <InfoRow label="Company" value={dealer.companyName} icon={<FaBuilding />} />
                  <InfoRow label="Registration Date" value={formatDate(dealer.registeredAt || dealer.createdAt)} icon={<FaClock />} />
                  <InfoRow label="Approved At" value={dealer.approvedAt ? formatDate(dealer.approvedAt) : null} icon={<FaCheck />} />
                  <InfoRow label="Tally Ledger" value={dealer.tallyLedger?.Name} icon={<FaBook />} />
                </div>
              </div>

              {/* Multi-Company Associations */}
              {dealer.companies && dealer.companies.length > 1 && (
                <div className={`${sectionBg} p-5 rounded-xl`}>
                  <h4 className={`font-semibold mb-4 flex items-center text-sm uppercase tracking-wide ${textPrimary}`}>
                    <FaBuilding className="mr-2 text-blue-500" />
                    Company Associations
                  </h4>
                  <div className="space-y-2">
                    {dealer.companies.map((c, i) => (
                      <div key={i} className={`flex items-center justify-between p-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <span className={`text-sm font-medium ${textPrimary}`}>{c.companyName || `Company ${i+1}`}</span>
                        <div className="flex items-center gap-2">
                          {c.isApproved && c.isActive && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>Active</span>
                          )}
                          {c.isApproved && !c.isActive && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>Suspended</span>
                          )}
                          {!c.isApproved && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>Pending</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Manager Details */}
            {m.hasManager && (
              <div className={`${sectionBg} p-5 rounded-xl`}>
                <h4 className={`font-semibold mb-4 flex items-center text-sm uppercase tracking-wide ${textPrimary}`}>
                  <FaUserTie className="mr-2 text-purple-500" />
                  Manager Details
                </h4>
                <div className="space-y-1">
                  <InfoRow label="Full Name" value={m.fullName} />
                  <InfoRow label="Mobile Number" value={m.mobileNumber} icon={<FaPhoneAlt />} />
                  <InfoRow label="Alternate Phone" value={m.alternatePhoneNumber} icon={<FaPhoneAlt />} />
                  <InfoRow label="Email" value={m.emailAddress} icon={<FaEnvelope />} />
                  <InfoRow label="Gender" value={m.gender} />
                  <InfoRow label="Date of Birth" value={m.dateOfBirth ? new Date(m.dateOfBirth).toLocaleDateString('en-IN') : null} />
                  <InfoRow label="Address" value={m.address} icon={<FaMapMarkerAlt />} />
                  <InfoRow label="Station" value={m.station?.name} icon={<FaMapMarkerAlt />} />
                  <InfoRow label="Aadhaar" value={m.aadhaar} icon={<FaIdCard />} />
                  <InfoRow label="PAN" value={m.pan} icon={<FaIdCard />} />
                </div>
              </div>
            )}

            {/* Business Details */}
            {(b.legalName || b.tradeName || b.gstin || b.pan) && (
              <div className={`${sectionBg} p-5 rounded-xl`}>
                <h4 className={`font-semibold mb-4 flex items-center text-sm uppercase tracking-wide ${textPrimary}`}>
                  <FaBriefcase className="mr-2 text-green-500" />
                  Business Details
                </h4>
                <div className="space-y-1">
                  <InfoRow label="Legal Name" value={b.legalName} />
                  <InfoRow label="Trade Name" value={b.tradeName} />
                  <InfoRow label="Station" value={b.station?.name} icon={<FaMapMarkerAlt />} />
                  <InfoRow label="Located in Main Station" value={b.isLocatedInMainStation === true ? 'Yes' : b.isLocatedInMainStation === false ? 'No' : null} />
                  {b.hasGSTIN && <InfoRow label="GSTIN" value={b.gstin} icon={<FaIdCard />} />}
                  {b.hasPAN && <InfoRow label="Business PAN" value={b.pan} icon={<FaIdCard />} />}
                  <InfoRow label="Registered Office Address" value={b.registeredOfficeAddress} icon={<FaMapMarkerAlt />} />
                  <InfoRow label="Business Phone" value={b.businessPhoneNumber} icon={<FaPhoneAlt />} />
                  <InfoRow label="Alt. Business Phone" value={b.alternativeBusinessPhoneNumber} icon={<FaPhoneAlt />} />
                  <InfoRow label="Business Email" value={b.businessEmailId} icon={<FaEnvelope />} />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={`mt-6 pt-4 border-t flex flex-wrap justify-end gap-3 ${borderColor}`}>
            {dealer.status === 'pending' && (
              <>
                <button
                  onClick={() => { onReject(dealer._id, dealer.dealerName); onClose(); }}
                  disabled={actionLoading === dealer._id}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center font-medium text-sm disabled:opacity-50 transition-colors"
                >
                  <FaTimes className="mr-2" /> Reject
                </button>
                <button
                  onClick={() => { onApprove(dealer._id, dealer.dealerName); onClose(); }}
                  disabled={actionLoading === dealer._id}
                  className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center font-medium text-sm disabled:opacity-50 transition-colors"
                >
                  <FaCheck className="mr-2" /> Approve
                </button>
              </>
            )}
            
            {dealer.status === 'approved' && (
              <button
                onClick={() => { onSuspend(dealer._id, dealer.dealerName); onClose(); }}
                disabled={actionLoading === dealer._id}
                className="px-5 py-2.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center font-medium text-sm disabled:opacity-50 transition-colors"
              >
                <FaToggleOff className="mr-2" /> Suspend Dealer
              </button>
            )}
            
            {dealer.status === 'suspended' && (
              <button
                onClick={() => { onActivate(dealer._id, dealer.dealerName); onClose(); }}
                disabled={actionLoading === dealer._id}
                className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center font-medium text-sm disabled:opacity-50 transition-colors"
              >
                <FaToggleOn className="mr-2" /> Activate Dealer
              </button>
            )}
            
            <button
              onClick={onClose}
              className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                darkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppTilesDealerManagement;
